import { track } from "./analytics";
/**
 * main.ts — landing page entry.
 *
 * Three-scene flow:
 *   1. #trivia-scene  — the 15-second question + three answer pills.
 *   2. #result-scene  — "CORRECT" / "NOT QUITE" + the fun fact + a
 *      single Next pill. Self-contained so the answer beat doesn't
 *      compete with the CTAs.
 *   3. #landing-scene — the original three CTAs (PLAY THE RACER /
 *      PLAY THE RUNNER / SHOP PS5) + boot hint. This is the page as
 *      it looked before the trivia gate was added; mounting it here
 *      keeps the trivia separated as a pre-step instead of mashed
 *      into the same screen as the choices.
 *
 * The trivia gates the *theatre*, not the experience — wrong / timeout
 * still flows through to the landing.
 */
(function () {
  "use strict";

  // ---------------------------------------------------------------- analytics
  // track() lives in analytics.ts — shared with racer.ts so every page
  // uses the same dataLayer contract.
  track("landing_view");

  // ---------------------------------------------------------------- toast
  const toastEl = document.getElementById("toast");
  let toastTimer: number | undefined;
  function toast(msg: string) {
    if (!toastEl) return;
    toastEl.textContent = msg;
    toastEl.hidden = false;
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => { toastEl.hidden = true; }, 2400);
  }

  // ---------------------------------------------------------------- trivia
  const QUESTION = {
    prompt: "What was the best-selling PlayStation 1 game of all time?",
    answers: ["Gran Turismo", "Final Fantasy VII", "Crash Bandicoot"],
    correct: 0,
    blurb: "Gran Turismo — 10.85 million copies sold on PS1.",
  } as const;

  const DURATION_MS = 15_000;

  const triviaScene = document.getElementById("trivia-scene");
  const resultScene = document.getElementById("result-scene");
  const landingScene = document.getElementById("landing-scene");
  const timerBarWrap = document.querySelector<HTMLElement>(".trivia-timer");
  const timerBar = document.querySelector<HTMLElement>(".trivia-timer__bar");
  const timerNum = document.getElementById("trivia-timer-num");
  const statusEl = document.getElementById("reveal-status");
  const copyEl = document.getElementById("reveal-copy");
  const nextBtn = document.getElementById("next-btn");

  let rafId = 0;
  let resolved = false;
  let elapsedMs = 0;

  function startTimer() {
    const t0 = performance.now();
    function tick(now: number) {
      if (resolved) return;
      elapsedMs = now - t0;
      const remain = Math.max(0, DURATION_MS - elapsedMs);
      const ratio = remain / DURATION_MS;
      if (timerBar) timerBar.style.setProperty("--t", String(ratio));
      if (timerNum) timerNum.textContent = String(Math.ceil(remain / 1000));
      if (timerBarWrap) {
        if (remain < 5000) timerBarWrap.setAttribute("data-urgent", "true");
        else timerBarWrap.removeAttribute("data-urgent");
      }
      if (remain <= 0) { resolve(-1); return; }
      rafId = requestAnimationFrame(tick);
    }
    rafId = requestAnimationFrame(tick);
  }

  function resolve(picked: number) {
    if (resolved) return;
    resolved = true;
    cancelAnimationFrame(rafId);
    const correct = picked === QUESTION.correct;
    // picked === -1 means the timer expired before the user tapped.
    // Fire a dedicated `trivia_timeout` event so the funnel report can
    // separate "ignored the trivia" from "answered wrong".
    if (picked < 0) {
      track("trivia_timeout", { timeMs: Math.round(elapsedMs) });
    } else {
      track("trivia_answer", { picked, correct, timeMs: Math.round(elapsedMs) });
    }

    // Paint per-pill states. The picked pill is what the user feels
    // most — keep it bright and pop it. Other answers fade lightly so
    // the picked pill commands the moment.
    const pills = document.querySelectorAll<HTMLButtonElement>(".pill--ans");
    pills.forEach((b, i) => {
      b.disabled = true;
      const isPicked = i === picked;
      const isCorrect = i === QUESTION.correct;
      if (isCorrect && isPicked)        b.dataset.state = "correct-picked";
      else if (isCorrect)               b.dataset.state = "correct-reveal";
      else if (isPicked && !isCorrect)  b.dataset.state = "wrong-picked";
      else                              b.dataset.state = "fade";
    });

    // Hold ~1500ms so the lock-in beat lands — the picked pill scales
    // up, fills with its accent color, and visibly *holds* selected
    // before the scene fades out.
    window.setTimeout(() => transitionToResult(correct), 1500);
  }

  function transitionToResult(correct: boolean) {
    if (!triviaScene || !resultScene || !statusEl || !copyEl) return;
    triviaScene.dataset.state = correct ? "answered-correct" : "answered-wrong";
    resultScene.setAttribute("data-result", correct ? "correct" : "wrong");

    // Wrap the status text in per-letter spans for the wave animation.
    const word = correct ? "CORRECT" : "NOT QUITE";
    statusEl.innerHTML = "";
    let letterIdx = 0;
    for (const ch of word) {
      const span = document.createElement("span");
      span.className = "letter";
      span.textContent = ch === " " ? " " : ch;
      // Each letter gets a CSS variable for its position in the wave.
      span.style.setProperty("--i", String(letterIdx));
      statusEl.appendChild(span);
      if (ch !== " ") letterIdx++;
    }
    copyEl.textContent = correct
      ? QUESTION.blurb
      : `The answer was ${QUESTION.blurb}`;

    // Let the trivia exit animation run, then unmount it and mount
    // the result scene. The landing scene stays hidden until Next.
    window.setTimeout(() => {
      triviaScene.hidden = true;
      resultScene.hidden = false;
    }, 520);
  }

  function transitionToLanding() {
    if (!resultScene || !landingScene) return;
    track("trivia_next");
    // Quick fade-out of the result, then mount the landing scene.
    resultScene.classList.add("scene-exiting");
    window.setTimeout(() => {
      resultScene.hidden = true;
      landingScene.hidden = false;
    }, 360);
  }

  document.querySelectorAll<HTMLButtonElement>(".pill--ans").forEach((b) => {
    b.addEventListener("click", () => {
      const idx = Number(b.dataset.answer);
      if (Number.isInteger(idx)) resolve(idx);
    });
  });

  nextBtn?.addEventListener("click", () => transitionToLanding());

  // CTA handlers — bind once; the markup is rendered up-front (just hidden).
  document.getElementById("racer-btn")?.addEventListener("click", () => {
    track("cta_click", { cta: "racer" });
  });
  document.getElementById("runner-btn")?.addEventListener("click", () => {
    track("cta_click", { cta: "runner" });
  });
  document.getElementById("shop-btn")?.addEventListener("click", (e) => {
    const a = e.currentTarget as HTMLAnchorElement;
    track("cta_click", { cta: "shop", href: a.href });
    if (a.getAttribute("href") === "#") {
      e.preventDefault();
      toast("Shop link coming soon");
    }
  });

  // Kick off the timer on next frame so the entrance animation can settle.
  requestAnimationFrame(() => startTimer());

})();
